import {Stack, Typography} from '@mui/material';
import React, {Fragment, useEffect, useMemo, useState} from 'react';

import {APIError} from 'data/errors/api-error';
import {HttpStatusCode} from 'data/protocols/http';
import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {AlertModal, CTAButtonFixed} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {FlowEnum} from 'presentation/pages/private/booking/constants';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {useLocationStore} from 'presentation/pages/private/store/locationStore';
import {FlowType} from 'presentation/pages/private/store/locationStore/constants';
import {translations} from 'presentation/translations-new';
import {getUserInfoFromToken} from 'presentation/utils';
import {GuidesEnum, QueryParamsEnum} from 'presentation/constants';
import {useNavigate} from 'react-router-dom';
import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {DistrictsProps} from '../../types';
import {DistrictErrorState} from '../DistrictErrorState';
import {DistrictList} from '../DistrictList';
import {EmptyState} from '../EmptyState';
import {NotFoundListCardAction} from '../NotFoundListCardAction';
import {SearchEmptyState} from '../SearchEmptyState';
import {StyledContainer} from './styles';
import {NotFoundVariantEnum} from '../NotFoundDialog/types';
import {GuideStep} from 'presentation/modules/guide/shared/constants/constants';
import {useDistrictsQuery} from 'presentation/modules/guide/shared/hooks/useDistrictsQuery';

const {
	loadingError,
	loadingState,
	notFoundModal: {cardListText},
	emptyModal: {part1, part2},
} = translations['pt-br'].privateFlow.bookingFlow.districtFlow;
const {districtEmptyState} = translations['pt-br'].privateFlow.guideFlow;
const {all, goForward, selectAll, notice, understood} = translations['pt-br'].commons;
const {notFoundAllGuideFlow} =
	translations['pt-br'].privateFlow.profile.locationSelection.emptySearchState;

export function Districts({flowType}: DistrictsProps) {
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {isMobile} = useMediaQueries();
	const navigate = useNavigate();

	const {query, districtNames} = SEARCH_PARAMS;
	const [showEmptyDistrictsModal, setShowEmptyDistrictsModal] = useState(false);
	const [isLoadingState, setIsLoadingState] = useState(true);

	const {data: healthCard, isLoading: healthCardIsLoading} = useGetHealthCardQuery();
	const cdPlanoANSOdonto =
		healthCard?.registroPlanoAnsOdonto?.trim() || healthCard?.registroPlanoAns;

	const cdPessoaC = getUserInfoFromToken().cdPessoa;
	const getLocation = useLocationStore(state => state.getLocation);
	const setLocation = useLocationStore(state => state.setLocation);
	const selectedLocation = getLocation(cdPessoaC, flowType);
	const {cdUf, nmUf} = selectedLocation.state;
	const nmCidade = selectedLocation.city;

	const {
		data: districtList,
		isLoading: districtListIsLoading,
		isError: districtListHasError,
		error: districtListError,
		refetch,
	} = useDistrictsQuery({
		flowType:
			flowType === FlowType.ODONTO_GUIDE ? FlowType.ODONTO_GUIDE : FlowType.HEALTH_GUIDE,
		healthParams: {cdUf, nmCidade},
		odontoParams: {cdUf, nmCidade, cdPlanoANS: cdPlanoANSOdonto ?? ''},
	});

	const selectedDistricts = useMemo(() => {
		if (!districtList) return [];

		const districts = selectedLocation.districtNames ?? [];
		return districtList
			.filter(item => districts.includes(item.bairro))
			.map(item => `${item.bairro}-${item.cidade}`);
	}, [selectedLocation.districtNames, districtList]);

	const allDistrictsSelected = useMemo(() => {
		if (!districtList) return false;
		return selectedDistricts.length === districtList.length;
	}, [selectedDistricts, districtList]);

	const districtListHasUnprocessableError =
		districtListHasError &&
		districtListError instanceof APIError &&
		districtListError.statusCode === HttpStatusCode.unprocessable;

	const NOT_FOUND =
		!districtListIsLoading &&
		(!districtList ||
			districtList.length === 0 ||
			(districtListHasError && districtListHasUnprocessableError));

	useEffect(() => {
		if (!districtListIsLoading) {
			setIsLoadingState(false);
		}
	}, [districtListIsLoading]);

	if (districtListHasError && !districtListHasUnprocessableError) {
		return <DistrictErrorState errorTitle={loadingError} handleRefresh={refetch} />;
	}

	if (isLoadingState || healthCardIsLoading) {
		return (
			<StyledContainer>
				<Loading text={loadingState} />
			</StyledContainer>
		);
	}

	if (NOT_FOUND) {
		return (
			<EmptyState
				flowType={flowType}
				title={districtEmptyState.title}
				description={districtEmptyState.description}
			/>
		);
	}

	const handleSelectCard = (district: string, skipScroll?: boolean) => {
		if (district === `${all}-${selectAll}`) {
			const shouldSelectAll = !allDistrictsSelected;
			const districts = districtList?.map(d => d.bairro) ?? [];

			setLocation(
				cdPessoaC,
				flowType === FlowType.ODONTO_GUIDE ? FlowType.ODONTO_GUIDE : FlowType.HEALTH_GUIDE,
				{
					...selectedLocation,
					districtNames: shouldSelectAll ? districts : [],
				},
			);

			if (!isMobile && shouldSelectAll && !skipScroll) {
				document.getElementById('go-forward-button')?.scrollIntoView({behavior: 'smooth'});
			}

			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.DISTRICT_NAMES);
					return prev;
				},
				{replace: true},
			);

			return;
		}

		const updatedSelected = selectedDistricts.includes(district)
			? selectedDistricts.filter(item => item !== district)
			: [...selectedDistricts, district];

		const districts = updatedSelected.map(item => item.split('-')[0]);

		setSearchParams(prev => {
			prev.set(QueryParamsEnum.DISTRICT_NAMES, districts.join(','));
			return prev;
		});
		setLocation(
			cdPessoaC,
			flowType === FlowType.ODONTO_GUIDE ? FlowType.ODONTO_GUIDE : FlowType.HEALTH_GUIDE,
			{
				...selectedLocation,
				districtNames: districts,
			},
		);
	};

	const handleGoToGuideFlow = () => {
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.UF_CODE, cdUf);
			prev.set(QueryParamsEnum.UF_NAME, nmUf);
			prev.set(QueryParamsEnum.CITY_NAME, nmCidade);
			return prev;
		});

		const baseUrl =
			flowType === FlowType.ODONTO_GUIDE
				? GuidesEnum.SERVICE_NETWORK_ODONTO
				: GuidesEnum.SERVICE_NETWORK_HEALTH;

		const params = new URLSearchParams({
			[QueryParamsEnum.STEP]: GuideStep.DETAILS,
			[QueryParamsEnum.UF_CODE]: cdUf,
			[QueryParamsEnum.UF_NAME]: nmUf,
			[QueryParamsEnum.CITY_NAME]: nmCidade,
		});

		if (districtNames) {
			params.append(QueryParamsEnum.DISTRICT_NAMES, districtNames);
		}

		navigate(`${baseUrl}?${params.toString()}`);
	};

	const handleGoForward = () => {
		if (selectedDistricts.length === 0) {
			setShowEmptyDistrictsModal(true);
			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.DISTRICT_NAMES);
					return prev;
				},
				{replace: true},
			);
			return;
		}
		handleGoToGuideFlow();
	};

	const FILTERED_DISTRICT_LIST =
		districtList?.filter(districtItem =>
			districtItem.bairro.toLowerCase().includes(query?.trim().toLowerCase() ?? ''),
		) ?? [];

	const showEmptyState = FILTERED_DISTRICT_LIST.length === 0;
	if (showEmptyState) {
		return (
			<SearchEmptyState
				description={notFoundAllGuideFlow}
				flowType={flowType === FlowType.ODONTO_GUIDE ? FlowEnum.ODONTO : FlowEnum.HEALTH}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_BAIRRO_ENCONTRADO}
			/>
		);
	}

	const pathByFlowType =
		flowType === FlowType.ODONTO_GUIDE
			? GuidesEnum.SERVICE_NETWORK_ODONTO_FLOW_CITY
			: GuidesEnum.SERVICE_NETWORK_HEALTH_FLOW_CITY;

	const pathCitySelection = `${pathByFlowType}?${QueryParamsEnum.UF_CODE}=${cdUf}&${QueryParamsEnum.UF_NAME}=${nmUf}`;

	return (
		<Fragment>
			<DistrictList
				list={FILTERED_DISTRICT_LIST}
				handleSelectCard={handleSelectCard}
				selectedDistricts={selectedDistricts}
				allDistrictsSelected={allDistrictsSelected}
			/>
			<Stack mt={2} id="go-forward-button">
				<NotFoundListCardAction
					flowType={
						flowType === FlowType.ODONTO_GUIDE ? FlowEnum.ODONTO : FlowEnum.HEALTH
					}
					customOrientationText={cardListText}
					variant={NotFoundVariantEnum.DISTRICT}
					pathGoTo={pathCitySelection}>
					<CTAButtonFixed.Root>
						<CTAButtonFixed.Action fullWidth onClick={handleGoForward} size="large">
							{goForward}
						</CTAButtonFixed.Action>
					</CTAButtonFixed.Root>
				</NotFoundListCardAction>
			</Stack>

			{showEmptyDistrictsModal ? (
				<AlertModal
					open={showEmptyDistrictsModal}
					headerTitle={notice}
					variant="info"
					onClose={() => setShowEmptyDistrictsModal(false)}
					buttonLabel={understood}
					slotProps={{
						paper: {
							style: {maxWidth: 420, width: '100%'},
						},
					}}
					subDescription={
						<Typography variant="h6" fontSize={18} textAlign="center" fontWeight={600}>
							{part1} <br /> {part2}
						</Typography>
					}
				/>
			) : null}
		</Fragment>
	);
}
